/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectRolePage.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-ROLES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectRolePage - Frontend UI module: ProjectRolePage.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блоки/логи для логики.
 * END_CHANGE_SUMMARY
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  getProjectRoles,
  createProjectRole,
  updateProjectRole,
  deleteProjectRole,
} from '../../api/projectRoleApi';
import { getProjects } from '../../api/projectApi';
import ProjectRole from '../../models/ProjectRole';
import Project from '../../models/Project';
import ProjectRoleList from './components/ProjectRoleList';
import CreateProjectRoleDialog from './components/CreateProjectRoleDialog';
import EditProjectRoleDialog from './components/EditProjectRoleDialog';
import DeleteProjectRoleDialog from './components/DeleteProjectRoleDialog';

/* START_METHOD_ProjectRolePage */
/* START_CONTRACT: ProjectRolePage
 * PURPOSE: Страница проектных ролей: список, создание, редактирование, удаление.
 * INPUTS: {} (projectId из useParams).
 * OUTPUTS: { JSX.Element - страница с списком и диалогами }.
 * SIDE_EFFECTS: HTTP getProjects/getProjectRoles/create/update/deleteProjectRole; навигация.
 * LINKS: M-FE-ROLES, M-FE-API
 * END_CONTRACT: ProjectRolePage
 */
export default function ProjectRolePage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const numericProjectId = Number(projectId);

  const [project, setProject] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);

  /* START_METHOD_fetchData */
  /* START_CONTRACT: fetchData
   * PURPOSE: Загрузить проект и его роли; обработать отсутствие проекта и ошибки.
   * INPUTS: { signal?: AbortSignal }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getProjects/getProjectRoles; setState.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: fetchData
   */
  const fetchData = useCallback(async (signal) => {
    if (!numericProjectId) {
      /* START_BLOCK_INVALID_ID */
      setError('Некорректный идентификатор проекта');
      setLoading(false);
      return;
      /* END_BLOCK_INVALID_ID */
    }

    try {
      const [projectsRes, rolesRes] = await Promise.all([
        getProjects(signal),
        getProjectRoles(numericProjectId, signal),
      ]);

      const foundProject = projectsRes.data
        .map(Project.fromResponse)
        .find((p) => p.id === numericProjectId);

      if (!foundProject) {
        /* START_BLOCK_PROJECT_NOT_FOUND */
        setError('Проект не найден');
        return;
        /* END_BLOCK_PROJECT_NOT_FOUND */
      }

      /* START_BLOCK_LOAD_OK */
      setProject(foundProject);
      setRoles(rolesRes.data.map(ProjectRole.fromResponse));
      setError('');
      /* END_BLOCK_LOAD_OK */
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      if (signal?.aborted) return;
      console.warn('[ProjectRolePage][fetchData][LOAD_FAIL] roles load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message || 'Ошибка загрузки проектных ролей');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [numericProjectId]);
  /* END_METHOD_fetchData */

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const refetch = () => fetchData();

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать проектную роль и обновить данные.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createProjectRole; refetch.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    await createProjectRole(numericProjectId, payload);
    await refetch();
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить проектную роль по id и обновить данные.
   * INPUTS: { id: number, payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateProjectRole; refetch.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (id, payload) => {
    await updateProjectRole(numericProjectId, id, payload);
    await refetch();
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить проектную роль по id и обновить данные.
   * INPUTS: { id: number }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteProjectRole; refetch.
   * LINKS: M-FE-ROLES
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async (id) => {
    await deleteProjectRole(numericProjectId, id);
    await refetch();
  };
  /* END_METHOD_handleDelete */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !project) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          size="small"
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          К проектам
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        onClick={() => navigate('/projects')}
        sx={{ mb: 2 }}
      >
        К проектам
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">
          Проектные роли — {project?.name}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Новая роль
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ProjectRoleList
        roles={roles}
        onEdit={(role) => {
          setEditingRole(role);
          setEditOpen(true);
        }}
        onDelete={(role) => {
          setDeletingRole(role);
          setDeleteOpen(true);
        }}
      />

      <CreateProjectRoleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        existingRoles={roles}
      />

      <EditProjectRoleDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditingRole(null);
        }}
        onUpdate={handleUpdate}
        role={editingRole}
        existingRoles={roles}
      />

      <DeleteProjectRoleDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingRole(null);
        }}
        onConfirm={handleDelete}
        role={deletingRole}
      />
    </Box>
  );
}
/* END_METHOD_ProjectRolePage */
